import { API_BASE_URL } from '@types';
import * as XLSX from 'xlsx';
import {
  buildV1ContactsExcelWorkbook,
  v1ContactsExcelFilename,
} from '../admin/legacyV1/buildV1UserExcel';

export type MigrationStatus =
  | 'pending'
  | 'ignored'
  | 'dry_run'
  | 'migrated'
  | 'failed';

export type LegacyMigrationInfo = {
  status: MigrationStatus;
  note: string | null;
  updated_at: string | null;
  new_user_id: number | null;
};

export type LegacyUserListItem = {
  id: number;
  name: string;
  email: string;
  display_name?: string;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
  email_verified_at: string | null;
  stripe_id: string | null;
  has_password: boolean;
  is_mfa_required: boolean | number | null;
  has_user_info: boolean;
  has_customer_data: boolean;
  has_bes_users_profile?: boolean;
  bes_type?: string | null;
  bes_first_name?: string | null;
  bes_last_name?: string | null;
  bes_full_name?: string | null;
  patients_count: number;
  courses_count: number;
  course_progress_count?: number;
  course_progress_old_count?: number;
  answers_count?: number;
  feedback_count?: number;
  course_tests_count?: number;
  courses_linked_count?: number;
  course_activity_count?: number;
  devices_count: number;
  configurations_count?: number;
  videos_count: number;
  food_lists_count?: number;
  food_list_items_count?: number;
  food_results_count?: number;
  professionals_count?: number;
  subscriptions_count?: number;
  payment_intents_count?: number;
  has_payment_intents?: boolean;
  migration: LegacyMigrationInfo;
};

export type LegacyUsersListResponse = {
  users: LegacyUserListItem[];
  pagination: {
    currentPage: number;
    pageSize: number;
    totalRecords: number;
    totalPages: number;
  };
};

export type LegacyUserDetail = {
  vapor_source?: {
    database: string;
    note: string;
  };
  user: Record<string, unknown>;
  user_info: Record<string, unknown> | null;
  customer_data: Record<string, unknown>[];
  professionals: Record<string, unknown>[];
  roles: Record<string, unknown>[];
  teams: Record<string, unknown>[];
  subscriptions: Record<string, unknown>[];
  subscription_items: Record<string, unknown>[];
  payment_intents: Record<string, unknown>[];
  patients: Record<string, unknown>[];
  patient_stats: {
    configurations_count: number;
    a_time_events_count: number;
    food_results_count: number;
  };
  configurations: Record<string, unknown>[];
  a_time_events: Record<string, unknown>[];
  a_time_events_truncated: boolean;
  food_results: Record<string, unknown>[];
  food_list_food: Record<string, unknown>[];
  devices: Record<string, unknown>[];
  device_connections: Record<string, unknown>[];
  courses: Record<string, unknown>[];
  course_progress: Record<string, unknown>[];
  course_progress_old: Record<string, unknown>[];
  /** Distinct catalog courses linked via entitlements/progress/answers/feedback */
  courses_linked?: Record<string, unknown>[];
  user_answers: Record<string, unknown>[];
  feedback: Record<string, unknown>[];
  course_tests?: Record<string, unknown>[];
  videos: Record<string, unknown>[];
  food_lists: Record<string, unknown>[];
  page_views: Record<string, unknown>[];
  personal_access_tokens: Record<string, unknown>[];
  bes_users?: Record<string, unknown> | null;
  migration: LegacyMigrationInfo;
};

export type LegacySchemaInventory = {
  database: string;
  table_count: number;
  users_count: number;
  users_soft_deleted: number;
  tables: {
    name: string;
    row_count: number;
    relation_kind?: string;
    link_via?: string | null;
    users_with_rows?: number | null;
    users_coverage_pct?: number | null;
  }[];
  no_user_relation_tables?: string[];
  note: string;
};

export type LegacyTableBrowseResult = {
  table: string;
  columns: string[];
  rows: Record<string, unknown>[];
  pagination: {
    currentPage: number;
    pageSize: number;
    totalRecords: number;
    totalPages: number;
  };
};

export const fetchLegacyUsers = async (
  page: number = 1,
  pageSize: number = 20,
  query?: string,
  migrationStatus:
    | 'all'
    | 'pending'
    | 'ignored'
    | 'migrated'
    | 'not_ignored' = 'all',
  configurations: 'all' | 'with' | 'without' = 'all'
): Promise<LegacyUsersListResponse> => {
  let url = `${API_BASE_URL}/services/v1/admin/legacy-users?page=${page}&size=${pageSize}`;
  if (query && query.trim().length > 0) {
    url += `&query=${encodeURIComponent(query.trim())}`;
  }
  if (migrationStatus !== 'all') {
    url += `&migrationStatus=${encodeURIComponent(migrationStatus)}`;
  }
  if (configurations !== 'all') {
    url += `&configurations=${encodeURIComponent(configurations)}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Failed to fetch legacy users (${response.status})`);
  }

  return (await response.json()) as LegacyUsersListResponse;
};

export const fetchLegacyUser = async (userId: number): Promise<LegacyUserDetail> => {
  const response = await fetch(
    `${API_BASE_URL}/services/v1/admin/legacy-users/${userId}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Failed to fetch legacy user (${response.status})`);
  }

  return (await response.json()) as LegacyUserDetail;
};

export const fetchLegacySchemaInventory =
  async (): Promise<LegacySchemaInventory> => {
    const response = await fetch(
      `${API_BASE_URL}/services/v1/admin/legacy-schema`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }
    );
    if (!response.ok) {
      const text = await response.text();
      throw new Error(
        text || `Failed to fetch legacy schema (${response.status})`
      );
    }
    return response.json();
  };

export const fetchLegacyTable = async (
  tableName: string,
  page: number = 1,
  pageSize: number = 50
): Promise<LegacyTableBrowseResult> => {
  const response = await fetch(
    `${API_BASE_URL}/services/v1/admin/legacy-schema/tables/${encodeURIComponent(tableName)}?page=${page}&size=${pageSize}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Failed to browse table (${response.status})`);
  }
  return response.json();
};

export const updateLegacyUserMigrationStatus = async (
  userId: number,
  status: MigrationStatus,
  note?: string | null
): Promise<{ migration: LegacyMigrationInfo }> => {
  const response = await fetch(
    `${API_BASE_URL}/services/v1/admin/legacy-users/${userId}/migration-status`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, note: note ?? null }),
    }
  );

  if (!response.ok) {
    const text = await response.text();
    throw new Error(
      text || `Failed to update migration status (${response.status})`
    );
  }

  return response.json();
};

/** Bulk: all migrated → pending (be2025 data kept). */
export const resetMigratedLegacyUsers = async (): Promise<{
  reset: number;
  legacy_user_ids: number[];
}> => {
  const response = await fetch(
    `${API_BASE_URL}/services/v1/admin/legacy-users/reset-migrated`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Failed to reset migrated users (${response.status})`);
  }
  return response.json();
};

export type LegacyMigrationRemaps = {
  courses: Array<{
    vapor_id: number;
    vapor_name: string;
    be_id: number;
    be_name: string;
    product_id: number | null;
  }>;
  videos: Array<{
    vapor_id: number;
    vapor_name: string;
    be_video_id: number;
    product_id: number;
    until_date: string | null;
  }>;
  answers_by_course: Array<{
    vapor_course_id: number;
    be_course_id: number;
    attempts: number;
    answer_keys_sample: string[];
  }>;
  patients?: Array<{ vapor_id: number; code: string }>;
  devices?: Array<{ vapor_id: number; name: string; type: string }>;
  configurations?: Array<{
    vapor_id: number;
    type: string;
    scenario: string;
    status: string;
  }>;
};

export type LegacyMigratePlanPatient = {
  vapor_patient_id: number;
  code: string;
  created_at: string | null;
  updated_at?: string | null;
  baseline?: number | null;
  variation?: number | null;
};

export type LegacyMigratePlanDevice = {
  vapor_device_id: number;
  name: string;
  type: string;
  model?: string | null;
  one_time_password?: string | null;
  created_at?: string | null;
};

export type LegacyMigratePlanConfiguration = {
  vapor_configuration_id: number;
  vapor_patient_id: number;
  vapor_device_id: number;
  type: 'exposure' | 'performance';
  scenario: string;
  status: string;
  config: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
  /** Session outcome counts (preview) */
  food_feedback_count?: number;
  hr_sample_count?: number;
  baseline?: number | null;
};

export type LegacyMigratePlanProfile = {
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  type: string | null;
  title: string | null;
  role: string | null;
  profession: number | null;
  disciple: number | null;
  contract: string | null;
  fiscal_code: string | null;
  order_of_psychologists: string | null;
  order_number: string | null;
  region: string | null;
  address: string | null;
  website: string | null;
  is_public: number | null;
  profile_photo_path: string | null;
  stripe_id: string | null;
  vat_number: string | null;
  sdi_number: string | null;
  created_at?: string | null;
};

export type LegacyMigratePlanCourse = {
  course_id: number;
  vapor_course_id: number;
  product_id: number | null;
  current_section: number | null;
  current_time: number | null;
  completed_at: string | null;
  passed_at: string | null;
  review: Record<string, unknown> | null;
  answer_1: unknown;
  answer_2: unknown;
  answer_3: unknown;
  answer_4: unknown;
  answer_5: unknown;
};

export type LegacyMigratePlanProduct = {
  product_id: number;
  until_date: string | null;
  source: 'course' | 'video' | 'exposure' | 'performance';
  vapor_ref_id: number;
};

export type LegacyMigratePreview = {
  dryRun: true;
  plan: {
    email: string;
    first_name: string | null;
    last_name: string | null;
    full_name: string | null;
    legacy_user_id: number;
    created_at?: string | null;
    profile: LegacyMigratePlanProfile;
    courses: LegacyMigratePlanCourse[];
    products: LegacyMigratePlanProduct[];
    patients?: LegacyMigratePlanPatient[];
    devices?: LegacyMigratePlanDevice[];
    configurations?: LegacyMigratePlanConfiguration[];
    remaps: LegacyMigrationRemaps;
    report: {
      coursesMapped: number;
      videosMapped: number;
      answersMapped: number;
      patientsMapped?: number;
      devicesMapped?: number;
      configurationsMapped?: number;
      configurationsExposure?: number;
      configurationsPerformance?: number;
      warnings: string[];
      unmappedCourses?: unknown[];
      unmappedVideos?: unknown[];
      unmappedQuestions?: unknown[];
    };
  };
};

export type LegacyMigrateApplyResult = {
  dryRun: false;
  migration: LegacyMigrationInfo;
  applied: {
    new_user_id: number;
    created_user: boolean;
    courses_upserted: number;
    products_upserted: number;
    patients_upserted?: number;
    devices_upserted?: number;
    configurations_upserted?: number;
  };
  report: {
    coursesMapped: number;
    videosMapped: number;
    answersMapped: number;
    patientsMapped?: number;
    configurationsMapped?: number;
  };
};

export const migrateLegacyUser = async (
  userId: number,
  options: { dryRun?: boolean } = {}
): Promise<LegacyMigratePreview | LegacyMigrateApplyResult> => {
  const dryRun = options.dryRun ? 'true' : 'false';
  const response = await fetch(
    `${API_BASE_URL}/services/v1/admin/legacy-users/${userId}/migrate?dryRun=${dryRun}`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  if (!response.ok) {
    const text = await response.text();
    let message = text || `Failed to migrate legacy user (${response.status})`;
    try {
      const parsed = JSON.parse(text) as { message?: string };
      if (parsed.message) message = parsed.message;
    } catch {
      // keep raw text
    }
    throw new Error(message);
  }

  return response.json();
};

export type LegacyUserMigrationExportPayload = {
  schema_version: number;
  kind: 'legacy_user_migration_export';
  legacy_source?: 'v1';
  exported_at: string;
  legacy_user_id: number;
  email: string;
  source: Record<string, unknown>;
  catalog: Record<string, unknown>;
};

const parseApiError = async (response: Response, fallback: string): Promise<string> => {
  const text = await response.text();
  let message = text || fallback;
  try {
    const parsed = JSON.parse(text) as { message?: string };
    if (parsed.message) message = parsed.message;
  } catch {
    // keep raw text
  }
  return message;
};

/** Download-ready portable JSON (source + vapor catalog snapshot). Requires OLD_DB. */
export const exportLegacyUserMigrationJson = async (
  userId: number
): Promise<LegacyUserMigrationExportPayload> => {
  const response = await fetch(
    `${API_BASE_URL}/services/v1/admin/legacy-users/${userId}/export-migration-json`,
    {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  if (!response.ok) {
    throw new Error(
      await parseApiError(
        response,
        `Failed to export migration JSON (${response.status})`
      )
    );
  }

  return response.json();
};

export const migrationExportFilename = (
  payload: Pick<LegacyUserMigrationExportPayload, 'legacy_user_id' | 'email'>
): string => {
  const emailSafe = payload.email.replace(/[^a-z0-9._-]+/gi, '_');
  return `legacy-migration-${payload.legacy_user_id}-${emailSafe}.json`;
};

/**
 * Prefer writing into repo `migration/v1/` via Vite dev middleware.
 * Falls back to browser download with the same relative path hint.
 */
export const saveLegacyUserMigrationExport = async (
  payload: LegacyUserMigrationExportPayload
): Promise<{ mode: 'filesystem' | 'download'; path: string }> => {
  const filename = migrationExportFilename(payload);
  const relativePath = `migration/v1/${filename}`;

  try {
    const response = await fetch('/__dev/migration-export', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename, payload }),
    });
    if (response.ok) {
      const result = (await response.json()) as { path?: string };
      return {
        mode: 'filesystem',
        path: result.path || relativePath,
      };
    }
  } catch {
    // Dev middleware unavailable (non-vite host) — fall through to download
  }

  const blob = new Blob([`${JSON.stringify(payload, null, 2)}\n`], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = relativePath;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { mode: 'download', path: relativePath };
};

/** Export + save JSON for legacy users matching the given list filters into migration/v1. */
export const generateAllMigrationJsonData = async (options?: {
  migrationStatus?: 'all' | 'pending' | 'ignored' | 'migrated' | 'not_ignored';
  configurations?: 'all' | 'with' | 'without';
  onProgress?: (info: {
    done: number;
    total: number;
    userId: number;
    email: string;
    ok: boolean;
    path?: string;
    error?: string;
  }) => void;
  signal?: AbortSignal;
}): Promise<{
  total: number;
  saved: number;
  failed: Array<{ userId: number; email: string; error: string }>;
}> => {
  const migrationStatus = options?.migrationStatus ?? 'pending';
  const configurations = options?.configurations ?? 'all';
  const pageSize = 100;
  let page = 1;
  let totalPages = 1;
  const users: Array<{ id: number; email: string }> = [];

  while (page <= totalPages) {
    if (options?.signal?.aborted) {
      throw new Error('Cancelled');
    }
    const batch = await fetchLegacyUsers(
      page,
      pageSize,
      undefined,
      migrationStatus,
      configurations
    );
    totalPages = Math.max(1, batch.pagination.totalPages);
    for (const user of batch.users) {
      users.push({ id: user.id, email: user.email });
    }
    page += 1;
  }

  const failed: Array<{ userId: number; email: string; error: string }> = [];
  let saved = 0;

  for (let i = 0; i < users.length; i += 1) {
    if (options?.signal?.aborted) {
      throw new Error('Cancelled');
    }
    const user = users[i];
    try {
      const payload = await exportLegacyUserMigrationJson(user.id);
      const result = await saveLegacyUserMigrationExport(payload);
      saved += 1;
      options?.onProgress?.({
        done: i + 1,
        total: users.length,
        userId: user.id,
        email: user.email,
        ok: true,
        path: result.path,
      });
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Export failed';
      failed.push({ userId: user.id, email: user.email, error });
      options?.onProgress?.({
        done: i + 1,
        total: users.length,
        userId: user.id,
        email: user.email,
        ok: false,
        error,
      });
    }
  }

  return { total: users.length, saved, failed };
};

/** One Excel download: sheet Utenti (courses + abbonamenti) + sheet Configs. */
export const generateAllLegacyV1MigrationExcelData = async (options?: {
  migrationStatus?: 'all' | 'pending' | 'ignored' | 'migrated' | 'not_ignored';
  configurations?: 'all' | 'with' | 'without';
  onProgress?: (info: {
    done: number;
    total: number;
    userId?: number;
    email?: string;
    ok: boolean;
    filename?: string;
    error?: string;
  }) => void;
  signal?: AbortSignal;
}): Promise<{
  total: number;
  downloaded: number;
  filename: string | null;
  failed: Array<{ userId: number; email: string; error: string }>;
}> => {
  const migrationStatus = options?.migrationStatus ?? 'pending';
  const configurations = options?.configurations ?? 'all';
  const pageSize = 100;
  let page = 1;
  let totalPages = 1;
  const users: Array<{ id: number; email: string }> = [];

  while (page <= totalPages) {
    if (options?.signal?.aborted) {
      throw new Error('Cancelled');
    }
    const batch = await fetchLegacyUsers(
      page,
      pageSize,
      undefined,
      migrationStatus,
      configurations
    );
    totalPages = Math.max(1, batch.pagination.totalPages);
    for (const user of batch.users) {
      users.push({ id: user.id, email: user.email });
    }
    page += 1;
  }

  const payloads: LegacyUserMigrationExportPayload[] = [];
  const failed: Array<{ userId: number; email: string; error: string }> = [];

  for (let i = 0; i < users.length; i += 1) {
    if (options?.signal?.aborted) {
      throw new Error('Cancelled');
    }
    const user = users[i];
    try {
      const payload = await exportLegacyUserMigrationJson(user.id);
      payloads.push(payload);
      options?.onProgress?.({
        done: i + 1,
        total: users.length,
        userId: user.id,
        email: user.email,
        ok: true,
      });
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Export failed';
      failed.push({ userId: user.id, email: user.email, error });
      options?.onProgress?.({
        done: i + 1,
        total: users.length,
        userId: user.id,
        email: user.email,
        ok: false,
        error,
      });
    }
  }

  if (payloads.length === 0) {
    return { total: users.length, downloaded: 0, filename: null, failed };
  }

  const filename = v1ContactsExcelFilename();
  const workbook = buildV1ContactsExcelWorkbook(payloads);
  XLSX.writeFile(workbook, filename);
  options?.onProgress?.({
    done: users.length,
    total: users.length,
    ok: true,
    filename,
  });

  return {
    total: users.length,
    downloaded: 1,
    filename,
    failed,
  };
};

/** Apply a previously exported JSON against live be2025 catalogs (no OLD_DB). */
export const migrateLegacyUserFromJson = async (
  payload: LegacyUserMigrationExportPayload | Record<string, unknown>,
  options: { dryRun?: boolean } = {}
): Promise<LegacyMigratePreview | LegacyMigrateApplyResult> => {
  const dryRun = options.dryRun ? 'true' : 'false';
  const response = await fetch(
    `${API_BASE_URL}/services/v1/admin/legacy-users/migrate-from-json?dryRun=${dryRun}`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    throw new Error(
      await parseApiError(
        response,
        `Failed to migrate from JSON (${response.status})`
      )
    );
  }

  return response.json();
};

/** Verify JSON → be2025 only (no vapor / OLD_DB). Prod-safe. */
export const verifyLegacyUserFromJson = async (
  payload: LegacyUserMigrationExportPayload | Record<string, unknown>
): Promise<LegacyMigrationVerification> => {
  const response = await fetch(
    `${API_BASE_URL}/services/v1/admin/legacy-users/verify-from-json`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    throw new Error(
      await parseApiError(
        response,
        `Failed to verify from JSON (${response.status})`
      )
    );
  }

  return response.json();
};

export type LegacyMigrationVerification = {
  ok: boolean;
  status: 'pass' | 'fail' | 'not_linked' | 'plan_failed';
  legacy_user_id: number;
  email: string;
  be_user_id: number | null;
  plan_ok: boolean;
  plan_error: string | null;
  summary: {
    courses_expected: number;
    courses_matched: number;
    products_expected: number;
    products_matched: number;
    vapor_linked_courses?: number;
    vapor_entitlements?: number;
    vapor_videos?: number;
    be_courses?: number;
    be_products?: number;
    content_fields_expected?: number;
    content_fields_matched?: number;
    out_of_scope_rows?: number;
    answers_keys_expected: number;
    answers_keys_matched: number;
    reviews_expected: number;
    reviews_matched: number;
    user_info_present: boolean;
    user_info_fields_expected: number;
    user_info_fields_matched: number;
    user_info_fields_kept?: number;
    progress_fields_expected: number;
    progress_fields_matched: number;
    mismatches: number;
  };
  mismatches: Array<{
    path: string;
    expected: unknown;
    actual: unknown;
    reason: string;
  }>;
  user_info: {
    present: boolean;
    source?: 'user_info' | 'professionals' | null;
    fields: Array<{
      vapor_field: string;
      be_field: string;
      expected: unknown;
      actual: unknown;
      ok: boolean;
      status?: 'matched' | 'kept_existing' | 'missing' | 'mismatch';
    }>;
  };
  progress: {
    fields: Array<{
      vapor_course_id: number;
      be_course_id: number;
      field: 'current_section' | 'current_time' | 'completed_at' | 'passed_at' | 'review';
      expected: unknown;
      actual: unknown;
      ok: boolean;
    }>;
  };
  inventory?: {
    counts: {
      vapor_entitlements: number;
      vapor_linked_courses: number;
      vapor_videos: number;
      plan_courses: number;
      plan_products_from_course: number;
      plan_products_from_video: number;
      plan_products: number;
      be_courses: number;
      be_products: number;
    };
    count_ok: {
      vapor_linked_vs_plan_courses: boolean;
      plan_vs_be_courses: boolean;
      vapor_videos_vs_plan_video_products: boolean;
      plan_vs_be_products: boolean;
    };
    warnings: string[];
    courses: Array<{
      vapor_course_id: number;
      vapor_name: string | null;
      sources: {
        entitlement: boolean;
        progress: boolean;
        progress_old: boolean;
        answers: boolean;
        feedback: boolean;
        tests: boolean;
      };
      be_course_id: number | null;
      product_id: number | null;
      in_plan: boolean;
      in_be: boolean;
      status: 'ok' | 'missing_in_be' | 'unmapped' | 'not_in_plan' | 'extra_in_be';
    }>;
    videos: Array<{
      vapor_video_id: number;
      vapor_name: string | null;
      be_video_id: number | null;
      product_id: number | null;
      in_plan: boolean;
      in_be: boolean;
      status: 'ok' | 'missing_in_be' | 'unmapped' | 'not_in_plan';
    }>;
    products: Array<{
      product_id: number;
      source: 'course' | 'video' | 'be_only';
      vapor_ref_id: number | null;
      in_plan: boolean;
      in_be: boolean;
      status: 'ok' | 'missing_in_be' | 'extra_in_be';
    }>;
    scope?: Array<{
      domain: string;
      vapor_table: string;
      count: number;
      in_migration_scope: boolean;
      note: string;
    }>;
    content?: Array<{
      domain: 'course' | 'product' | 'profile_plan';
      path: string;
      vapor_ref: string | number | null;
      field: string;
      expected: unknown;
      actual: unknown;
      ok: boolean;
      status: 'matched' | 'kept_or_ahead' | 'missing' | 'mismatch';
    }>;
  } | null;
  report: {
    coursesMapped: number;
    videosMapped: number;
    answersMapped: number;
    warnings: string[];
  } | null;
  remaps: LegacyMigrationRemaps | null;
  migration_log: {
    status: string;
    new_user_id: number | null;
    updated_at: string | null;
  } | null;
  verify_mode?: 'vapor_to_be' | 'json_to_be';
  vapor_to_json?: {
    ok: boolean;
    legacy_user_id: number;
    email: string;
    export_exported_at: string | null;
    summary: {
      fields_checked: number;
      fields_matched: number;
      mismatches: number;
    };
    mismatches: Array<{
      path: string;
      expected: unknown;
      actual: unknown;
      reason: string;
    }>;
    counts: {
      vapor: Record<string, number>;
      json: Record<string, number>;
    };
  } | null;
};

/** Load exported JSON from local repo migration/v1 (Vite dev middleware). */
export const loadLocalMigrationExport = async (
  legacyUserId: number
): Promise<{ path: string; payload: LegacyUserMigrationExportPayload }> => {
  const response = await fetch(
    `/__dev/migration-export?legacyUserId=${legacyUserId}`,
    { method: 'GET', headers: { 'Content-Type': 'application/json' } }
  );
  if (!response.ok) {
    throw new Error(
      await parseApiError(
        response,
        `No local export in migration/v1 for #${legacyUserId}`
      )
    );
  }
  return response.json();
};

export const verifyLegacyUserMigration = async (
  userId: number,
  options: { exportPayload?: LegacyUserMigrationExportPayload | null } = {}
): Promise<LegacyMigrationVerification> => {
  const hasExport = Boolean(options.exportPayload);
  const response = await fetch(
    `${API_BASE_URL}/services/v1/admin/legacy-users/${userId}/verify-migration`,
    {
      method: hasExport ? 'POST' : 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: hasExport ? JSON.stringify(options.exportPayload) : undefined,
    }
  );

  if (!response.ok) {
    throw new Error(
      await parseApiError(
        response,
        `Failed to verify migration (${response.status})`
      )
    );
  }

  return response.json();
};

export type LegacyMigratedBatchVerifyFailure = {
  legacy_user_id: number;
  be_user_id: number | null;
  email: string;
  status: string;
  mismatches: number;
  first_reason: string | null;
  out_of_scope_rows: number;
};

export type LegacyMigratedBatchVerifyResult = {
  ok: boolean;
  scanned: number;
  passed: number;
  failed: number;
  missing_legacy: number;
  failing_legacy_user_ids: number[];
  first_failing_legacy_user_id: number | null;
  failing: LegacyMigratedBatchVerifyFailure[];
};

export const verifyAllMigratedLegacyUsers =
  async (): Promise<LegacyMigratedBatchVerifyResult> => {
    const response = await fetch(
      `${API_BASE_URL}/services/v1/admin/legacy-users/verify-migrated`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    if (!response.ok) {
      const text = await response.text();
      let message = text || `Failed to verify migrated users (${response.status})`;
      try {
        const parsed = JSON.parse(text) as { message?: string };
        if (parsed.message) message = parsed.message;
      } catch {
        // keep raw text
      }
      throw new Error(message);
    }

    return response.json();
  };

export type LegacyCatalogMapResponse = {
  courses: Array<{
    vapor_id: number;
    vapor_name: string;
    be_id: number;
    be_name: string;
    product_id: number | null;
  }>;
  videos: Array<{
    vapor_id: number;
    vapor_name: string;
    vapor_category: string | null;
    be_id: number;
    be_name: string;
    be_category: string | null;
    product_id: number | null;
  }>;
  questions: Array<{
    vapor_id: number;
    be_id: number;
    vapor_course_id: number;
    be_course_id: number;
    match: 'id' | 'description';
  }>;
  unmapped: {
    courses: Array<{ vaporId: number; name: string }>;
    videos: Array<{ vaporId: number; name: string; category: string | null }>;
    questions: Array<{ vaporId: number; courseId: number; description: string }>;
  };
  summary: {
    courses_mapped: number;
    courses_unmapped: number;
    videos_mapped: number;
    videos_unmapped: number;
    questions_mapped: number;
    questions_unmapped: number;
    id_coincidence_courses: number;
    id_divergence_videos: number;
  };
};

export const fetchLegacyCatalogMap =
  async (): Promise<LegacyCatalogMapResponse> => {
    const response = await fetch(
      `${API_BASE_URL}/services/v1/admin/legacy-users/catalog-map`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }
    );
    if (!response.ok) {
      const text = await response.text();
      throw new Error(
        text || `Failed to fetch catalog map (${response.status})`
      );
    }
    return response.json();
  };

export const downloadMigratedLegacyUsersExcel = async (): Promise<void> => {
  const response = await fetch(
    `${API_BASE_URL}/services/v1/admin/legacy-users/export/migrated`,
    {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Failed to export migrated users (${response.status})`);
  }

  const payload = (await response.json()) as {
    filename?: string;
    contentBase64?: string;
    users_count?: number;
  };

  if (!payload.contentBase64) {
    throw new Error('Export payload missing contentBase64');
  }

  if ((payload.users_count ?? 0) === 0) {
    throw new Error('No migrated users to export');
  }

  const binary = atob(payload.contentBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const filename = payload.filename || 'legacy-migrated.xlsx';
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};
